<template>
  <LoadingIcon v-if="isLoading" />
  <Card
    v-else
    :class="$style.card"
  >
    <template v-slot:header>
      <HeadingExtraSmall>
        {{ displayName }}
        <ButtonLink
          :class="$style.link"
          data-test-id="MDMCertificateWidget__viewDetails"
          text="View MDM"
          @click="actionButtonHandler()"
        />
      </HeadingExtraSmall>
    </template>
    <template v-slot:body>
      <GridAuto
        :class="$style.noDataDiv"
        minWidth="20rem"
      >
        <div
          v-for="certificate in certificateDetails"
          :key="certificate.label"
          :class="$style.outerDiv"
        >
          <div v-if="!certificate.noData">
            <ChartWrapper
              :class="$style.innerChart"
              data-test-id="MDMCertificateWidget__charts"
              :options="options(certificate)"
              :series="certificate.series"
              :type="type"
            />
            <InputLabel
              :class="$style.label"
              :text="certificate.labels[0]"
            />
          </div>
          <div v-else>
            <Card
              v-if="noDataCertificateDetails.length === 1"
              :class="$style.innerConfigCard"
              type="informational"
            >
              <template v-slot:header>
                <HeadingExtraSmall>
                  <Icon
                    :class="$style.iconInner"
                    icon="info"
                  />
                  <Link
                    :class="$style.setupLink"
                    href="#/mdm"
                    text="Complete Setup"
                  />
                </HeadingExtraSmall>
              </template>
            </Card>
            <ChartWrapper
              :class="$style.outerChart"
              data-test-id="MDMCertificateWidget__charts"
              :options="options(certificate)"
              :series="certificate.series"
              :type="type"
            />
            <InputLabel
              :class="$style.noDataLabel"
              :text="certificate.labels[0]"
            />
          </div>
        </div>
        <Card
          v-if="noDataCertificateDetails.length >1"
          :class="$style.outerConfigCard"
          type="informational"
        >
          <template v-slot:header>
            <HeadingExtraSmall>
              <Icon
                :class="$style.icon"
                icon="info"
              />
              <Link
                :class="$style.setupLink"
                href="#/mdm"
                text="Complete Setup"
              />
            </HeadingExtraSmall>
          </template>
        </Card>
      </GridAuto>
    </template>
  </Card>
</template>

<script>
import {
  ButtonLink,
  Card,
  GridAuto,
  HeadingExtraSmall,
  Icon,
  InputLabel,
  Link,
  LoadingIcon,
} from '@jumpcloud/ui-components';
import {
  ChartWrapper,
} from '@jumpcloud/ui-components/charts';
import pluralize from 'pluralize';

const percentage = 100;

export default {
  name: 'RadialChartWrapper',
  components: {
    ButtonLink,
    Card,
    ChartWrapper,
    GridAuto,
    HeadingExtraSmall,
    Icon,
    InputLabel,
    Link,
    LoadingIcon,
  },
  props: {
    actionButtonHandler: {
      type: Function,
      default: () => {},
    },
    certificateDetails: {
      type: Array,
      default: () => ([]),
    },
    displayName: {
      required: true,
      type: String,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    type: {
      required: true,
      type: String,
    },
  },
  data: () => ({
    labelColors: ['jcDataVizG', 'jcDataVizF', 'jcDataVizF', 'var(--jcGrey)', 'jcDataVizE'],
    fillColors: ['var(--jc-color-success-strong)', 'var(--jc-color-warning-base)', 'var(--jc-color-warning-strong)', 'var(--jc-color-neutral-stroke)', 'var(--jc-color-error-base)'],
  }),

  computed: {
    noDataCertificateDetails() {
      const data = [];
      this.certificateDetails.forEach((item) => {
        if (item.noData) { data.push(item); }
      });
      return data;
    },
  },

  methods: {
    getFillColor(certificate, fillColors) {
      const { daysSeries, noData } = certificate;
      const [green, yellow, orange, grey, red] = fillColors;
      let color = '';
      switch (true) {
        case daysSeries <= 365 && daysSeries > 90:
          color = green;
          break;
        case daysSeries <= 90 && daysSeries > 60:
          color = yellow;
          break;
        case daysSeries <= 60 && daysSeries > 30:
          color = orange;
          break;
        case daysSeries === 0 && noData:
          color = grey;
          break;
        case (daysSeries === 0 && !noData) || (daysSeries < 0 || daysSeries <= 30):
          color = red;
          break;
        default:
          break;
      }
      return color;
    },

    options(certificate) {
      const options = {
        chart: {
          type: 'radialBar',
          offsetY: -10,
          sparkline: {
            enabled: true,
          },
        },
        plotOptions: {
          radialBar: {
            startAngle: -90,
            endAngle: 90,
            track: {
              margin: 5,
            },
            dataLabels: {
              name: {
                show: false,
              },
              value: {
                offsetY: -20,
                fontSize: '15px',
              },
            },
          },
        },
        grid: {
          padding: {
            top: -10,
          },
        },
      };

      options.fill = {
        colors: [this.getFillColor(certificate, this.fillColors)],
      };
      options.labels = certificate.labels;
      if (!certificate.noData) {
        options.plotOptions.radialBar.dataLabels.value.formatter = (dayAsPercentage) => {
          const day = ((percentage - dayAsPercentage) / percentage) * 365;
          const noOfDays = day <= 0 ? (Math.abs(day) + 1) : day;
          const pluralizedDays = `${pluralize('day', Math.round(Math.abs(noOfDays)), true)}`;
          return day <= 0 ? `${pluralizedDays} ago` : pluralizedDays;
        };
      } else {
        options.plotOptions.radialBar.dataLabels.value.show = false;
      }

      return options;
    },
  },
};
</script>

<style module scoped>
@value (cardShadow1) from '~css/shadows.css';

.card {
  box-shadow: cardShadow1;
}

.icon {
  color: var(--jc-color-informational-base);
  padding-right: 2.3rem;
}

.iconInner {
  color: var(--jc-color-informational-base);
  padding-right: var(--jc-spacer);
}

.link:not(.legacyOverride):not(.legacyOverride) {
  float: right;
  font-weight: 400;
}

.label:not(.legacyOverride):not(.legacyOverride) {
  color: var(--jc-color-neutral-strong);
  display: flex;
  font-weight: 400;
  justify-content: center;
}

.noDataLabel:not(.legacyOverride):not(.legacyOverride) {
  color: var(--jc-color-neutral-text-descriptive);
  display: flex;
  font-weight: 400;
  justify-content: center;
}

.outerDiv:not(.legacyOverride):not(.legacyOverride) {
  align-items: center;
  display: flex;
}

.noDataDiv:not(.legacyOverride):not(.legacyOverride) {
  display: flex;
  position: relative;
}

.innerConfigCard:not(.legacyOverride):not(.legacyOverride) {
  margin-left: 3.7rem;
  margin-top: 4rem;
  min-height: 3rem;
  min-width: 15.7rem;
  padding: var(--jc-spacer-small) var(--jc-spacer);
  position: absolute;
  z-index: 10;

  --card-padding: var(--jc-spacer-small);
  --card-border-color: var(--jc-color-neutral-stroke);
}

.outerConfigCard:not(.legacyOverride):not(.legacyOverride) {
  margin-left: 39rem;
  margin-top: 4rem;
  min-height: 3rem;
  min-width: 18rem;
  padding: var(--jc-spacer-small) var(--jc-spacer);
  position: absolute;

  --card-padding: var(--jc-spacer-small);
  --card-border-color: var(--jc-color-neutral-stroke);
}

.setupLink:not(.legacyOverride):not(.legacyOverride) {
  font-size: var(--jcHeadingExtraSmall);
}

@media screen and (max-width: 1024px) {
  .noDataDiv:not(.legacyOverride):not(.legacyOverride) {
    display: flex;
    position: relative;
  }

  .innerConfigCard:not(.legacyOverride):not(.legacyOverride) {
    margin-left: 2rem;
    margin-top: 3rem;
    min-height: 3rem;
    min-width: 15.7rem;
    padding: var(--jc-spacer-small) var(--jc-spacer);
    position: absolute;
    z-index: 10;

    --card-padding: var(--jc-spacer-small);
    --card-border-color: var(--jc-color-neutral-stroke);
  }

  .outerConfigCard:not(.legacyOverride):not(.legacyOverride) {
    margin-left: 33.3rem;
    margin-top: 3rem;
    min-height: 3rem;
    min-width: 17rem;
    padding: var(--jc-spacer-small) var(--jc-spacer);
    position: absolute;

    --card-padding: var(--jc-spacer-small);
    --card-border-color: var(--jc-color-neutral-stroke);
  }

  .outerChart:not(.legacyOverride):not(.legacyOverride) {
    --chart-width: 20rem;
  }

  .innerChart:not(.legacyOverride):not(.legacyOverride) {
    --chart-width: 20rem;
  }

  .setupLink:not(.legacyOverride):not(.legacyOverride) {
    font-size: var(--jcHeadingExtraSmall);
  }
}

@media screen and (max-width: 768px) {
  .noDataDiv:not(.legacyOverride):not(.legacyOverride) {
    display: flex;
    position: relative;
  }

  .innerConfigCard:not(.legacyOverride):not(.legacyOverride) {
    margin-left: 1.5rem;
    margin-top: 1.5rem;
    min-height: 2.5rem;
    min-width: 12rem;
    padding: var(--jc-spacer-x-small) var(--jc-spacer-small);
    position: absolute;
    z-index: 10;

    --card-padding: var(--jc-spacer-small);
    --card-border-color: var(--jc-color-neutral-stroke);
  }

  .outerConfigCard:not(.legacyOverride):not(.legacyOverride) {
    margin-left: 25.6rem;
    margin-top: 2rem;
    min-height: 2rem;
    min-width: 12.5rem;
    padding: var(--jc-spacer-x-small) var(--jc-spacer-small);
    position: absolute;

    --card-padding: var(--jc-spacer-small);
    --card-border-color: var(--jc-color-neutral-stroke);
  }

  .outerChart:not(.legacyOverride):not(.legacyOverride) {
    --chart-width: 15rem;
  }

  .innerChart:not(.legacyOverride):not(.legacyOverride) {
    --chart-width: 15rem;
  }

  .icon {
    color: var(--jc-color-informational-base);
    padding-right: 1rem;
  }

  .setupLink:not(.legacyOverride):not(.legacyOverride) {
    font-size: 0.9rem;
  }
}

</style>
